import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

// ─── Configuration ──────────────────────────────────────────────────────────

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "";
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID ?? "";

const userPool =
  USER_POOL_ID && CLIENT_ID
    ? new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID })
    : null;

// ─── Auth Context ───────────────────────────────────────────────────────────

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { email: string } | null;
  idToken: string | null;
  /** Sign in with email + password. Returns object with newPasswordRequired flag if user must change password. */
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ newPasswordRequired: boolean }>;
  /** Complete the new-password challenge (when newPasswordRequired was true). */
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  idToken: null,
  signIn: async () => ({ newPasswordRequired: false }),
  completeNewPassword: async () => {},
  logout: () => {},
});

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return Date.now() >= payload.exp * 1000;
}

// ─── Auth Provider ──────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [user, setUser] = useState<{ email: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Track the in-flight cognito user that needs new password challenge
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);

  // Restore session from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("id_token");
    if (stored && !isTokenExpired(stored)) {
      setIdToken(stored);
      const payload = parseJwt(stored);
      setUser({ email: (payload?.email as string) ?? "admin" });
    } else {
      localStorage.removeItem("id_token");
    }
    setIsLoading(false);
  }, []);

  const setSessionFromCognito = useCallback((session: CognitoUserSession) => {
    const token = session.getIdToken().getJwtToken();
    localStorage.setItem("id_token", token);
    setIdToken(token);
    const payload = parseJwt(token);
    setUser({ email: (payload?.email as string) ?? "admin" });
  }, []);

  const signIn = useCallback(
    (email: string, password: string): Promise<{ newPasswordRequired: boolean }> => {
      return new Promise((resolve, reject) => {
        if (!userPool) {
          reject(new Error("Cognito 未配置"));
          return;
        }

        const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            setSessionFromCognito(session);
            setPendingUser(null);
            resolve({ newPasswordRequired: false });
          },
          onFailure: (err) => {
            setPendingUser(null);
            reject(err);
          },
          newPasswordRequired: () => {
            // First-time login — must change password
            setPendingUser(cognitoUser);
            resolve({ newPasswordRequired: true });
          },
        });
      });
    },
    [setSessionFromCognito],
  );

  const completeNewPassword = useCallback(
    (newPassword: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (!pendingUser) {
          reject(new Error("没有待修改密码的用户"));
          return;
        }
        pendingUser.completeNewPasswordChallenge(
          newPassword,
          {},
          {
            onSuccess: (session) => {
              setSessionFromCognito(session);
              setPendingUser(null);
              resolve();
            },
            onFailure: (err) => {
              reject(err);
            },
          },
        );
      });
    },
    [pendingUser, setSessionFromCognito],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("id_token");
    setIdToken(null);
    setUser(null);
    setPendingUser(null);
    if (userPool) {
      const current = userPool.getCurrentUser();
      current?.signOut();
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!idToken,
        isLoading,
        user,
        idToken,
        signIn,
        completeNewPassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
